/**
 * Dropdown for loading saved filter presets.
 */

import { useState } from 'react'
import { FolderOpen, Star, Users, Trash2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useStudyStore } from '@/stores/studyStore'
import { useSavedFilters } from '@/hooks/useFilters'
import { useAuthContext } from '@/hooks/AuthContext'
import { cn } from '@/lib/utils'

interface LoadFilterDropdownProps {
  pageKey?: string
  className?: string
}

export function LoadFilterDropdown({ pageKey, className }: LoadFilterDropdownProps) {
  const { user } = useAuthContext()
  const currentStudy = useStudyStore((state) => state.currentStudy)
  const {
    savedFilters,
    isLoading,
    applyFilter,
    deleteFilter,
    isDeleting,
  } = useSavedFilters(currentStudy?.studyId || null, pageKey)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [filterToDelete, setFilterToDelete] = useState<{ id: string; name: string } | null>(null)
  const [isApplying, setIsApplying] = useState(false)

  // Separate filters into user's own and shared
  const myFilters = savedFilters.filter((f) => f.createdBy === user?.id)
  const sharedFilters = savedFilters.filter((f) => f.createdBy !== user?.id && f.isShared)

  const handleApply = async (filterId: string) => {
    setIsApplying(true)
    try {
      await applyFilter(filterId)
    } finally {
      setIsApplying(false)
    }
  }

  const handleDeleteClick = (e: React.MouseEvent, filter: { id: string; name: string }) => {
    e.stopPropagation()
    e.preventDefault()
    setFilterToDelete(filter)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (filterToDelete) {
      deleteFilter(filterToDelete.id)
      setDeleteDialogOpen(false)
      setFilterToDelete(null)
    }
  }

  if (!currentStudy) {
    return null
  }

  const hasFilters = savedFilters.length > 0

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn('h-7 gap-1 text-muted-foreground', className)}
            disabled={isLoading || isApplying}
          >
            {(isLoading || isApplying) ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <FolderOpen className="h-3 w-3" />
            )}
            <span className="hidden sm:inline">Saved</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {!hasFilters ? (
            <div className="py-4 px-2 text-center">
              <p className="text-sm text-muted-foreground">No saved filters</p>
              <p className="text-xs text-muted-foreground mt-1">
                Save your current filters to access them quickly later.
              </p>
            </div>
          ) : (
            <>
              {/* My Filters */}
              {myFilters.length > 0 && (
                <>
                  <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider">
                    My Filters
                  </DropdownMenuLabel>
                  {myFilters.map((filter) => (
                    <DropdownMenuItem
                      key={filter.id}
                      className="flex items-center justify-between cursor-pointer group"
                      onClick={() => handleApply(filter.id)}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="truncate">{filter.name}</span>
                        {filter.isDefault && (
                          <Star className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0" />
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 shrink-0"
                        onClick={(e) => handleDeleteClick(e, { id: filter.id, name: filter.name })}
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </DropdownMenuItem>
                  ))}
                </>
              )}

              {/* Shared Filters */}
              {sharedFilters.length > 0 && (
                <>
                  {myFilters.length > 0 && <DropdownMenuSeparator />}
                  <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider">
                    Shared Filters
                  </DropdownMenuLabel>
                  {sharedFilters.map((filter) => (
                    <DropdownMenuItem
                      key={filter.id}
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={() => handleApply(filter.id)}
                    >
                      <Users className="h-3 w-3 text-muted-foreground shrink-0" />
                      <span className="truncate">{filter.name}</span>
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Filter</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{filterToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
